import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { IdCard, MapPin, Phone, X } from 'lucide-react';
import { isValidElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import StatusChip from '../../component/common/StatusChip';
import usePermissions from '../../hooks/usePermissions';

// Detalhe do fornecedor — side drawer somente-leitura, DESIGN-SYSTEM.md §5.6
// "Side drawer" (slide da direita, largura fixa, dispensa por clique no
// scrim ou no X), mesmo padrão do drawer de visualização de
// `pages/products/List.js`, preenchido com os cartões `field-card`/`ro-grid`
// de `prototipo-lista-fornecedores.html`. `personType` (PJ/PF) não existe
// como coluna em `suppliers` (fv-api) — inferido no cliente a partir da
// quantidade de dígitos do documento, igual a `suppliers/Form.js`.
function RoField({ label, value, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {isValidElement(value) ? (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            ) : (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: value || value === 0 ? 'text.primary' : 'text.disabled', lineHeight: 1.4 }}>
                    {value || value === 0 ? value : '—'}
                </Typography>
            )}
        </Box>
    );
}

function FieldCard({ icon: Icon, title, children }) {
    return (
        <Box sx={{ backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: '16px 18px', mb: '14px', '&:last-child': { mb: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '12px' }}>
                <Icon size={14} color="currentColor" style={{ opacity: 0.55 }} />
                <Typography sx={{ fontSize: '12.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.secondary' }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                {children}
            </Box>
        </Box>
    );
}

function inferPersonType(document) {
    const digits = (document || '').replace(/\D/g, '');
    return digits.length === 11 ? 'pf' : 'pj';
}

export default function SupplierViewDialog({ open, onClose, supplierId }) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [loading, setLoading] = useState(true);
    const [supplier, setSupplier] = useState({});

    const fetchSupplier = async (id) => {
        try {
            setLoading(true);
            const response = await httpClient.get(`/suppliers/${id}`);
            setSupplier(response.data || {});
        } catch (error) {
            console.error('Erro ao carregar dados do fornecedor:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (supplierId && open) {
            fetchSupplier(supplierId);
        }
    }, [supplierId, open]);

    const handleClose = () => {
        setSupplier({});
        setLoading(true);
        onClose();
    };

    const personType = inferPersonType(supplier.document);
    const isPJ = personType === 'pj';

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}
        >
            <Box sx={{ p: '20px 22px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, mb: '4px' }} noWrap>
                        {loading ? 'Carregando…' : (supplier.legal_name || 'Detalhes do fornecedor')}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        Prévia somente leitura
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flexDirection: 'column' }}>
                        <CircularProgress size={44} thickness={4} />
                        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                            Carregando informações do fornecedor...
                        </Typography>
                    </Box>
                ) : (
                    <Fade in={!loading} timeout={400}>
                        <Box>
                            <FieldCard icon={IdCard} title="Dados do fornecedor">
                                <RoField label="Tipo" value={<StatusChip domain="personType" value={personType} />} />
                                <RoField label={isPJ ? 'CNPJ' : 'CPF'} value={supplier.document} />
                                <RoField full label={isPJ ? 'Razão Social' : 'Nome completo'} value={supplier.legal_name} />
                                {isPJ && <RoField full label="Nome Fantasia" value={supplier.trade_name} />}
                            </FieldCard>

                            <FieldCard icon={Phone} title="Contato">
                                <RoField label="Celular" value={supplier.cell_phone} />
                                <RoField label="Telefone" value={supplier.phone_number} />
                                <RoField full label="E-mail" value={supplier.email} />
                                <RoField full label="Site" value={supplier.website} />
                            </FieldCard>

                            <FieldCard icon={MapPin} title="Endereço">
                                <RoField label="CEP" value={supplier.zipcode} />
                                <RoField full label="Rua" value={supplier.address} />
                                <RoField label="Número" value={supplier.number_address} />
                                <RoField label="Complemento" value={supplier.complement} />
                                <RoField label="Bairro" value={supplier.neighborhood} />
                                <RoField label="Cidade" value={supplier.city} />
                            </FieldCard>
                        </Box>
                    </Fade>
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                <Button variant="outlined" onClick={handleClose} size="small">Fechar</Button>
                {hasPermission('supplier.update') && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            const targetId = supplierId;
                            handleClose();
                            navigate(`/suppliers/edit/${targetId}`);
                        }}
                    >
                        Editar
                    </Button>
                )}
            </Box>
        </Drawer>
    );
}
